/**
 * El armazón autenticado: cabecera + flujo en vivo + salida.
 *
 * Aquí vive el `ClienteVivo`: se conecta al montar y se corta al desmontar. El
 * indicador de conexión se re-evalúa cada pocos segundos para que el cinturón de
 * "socket encallado" (por tiempo) también salte, no solo los eventos.
 */
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { Link, Navigate, NavLink, Outlet, useLocation, useNavigate, useParams } from 'react-router'

import { CLAVE_SESION, useSesion } from './auth'
import { ConnectionIndicator } from './components/ConnectionIndicator'
import { HilosFlotantesProvider } from './components/HilosFlotantes'
import { RefrescoCabecera } from './components/RefrescoCabecera'
import { Button } from './components/ui/button'
import { MenuMano } from './menu-mano'
import { clasificarConexion, type SnapshotConexion } from './lib/conexion'
import { api } from './lib/api'
import { ClienteVivo, CLAVE_CONVERSACIONES } from './lib/live'
import { esRutaDeOperacion } from './lib/ver-como'
import { migaAnterior, SubmenuNegocio } from './routes/negocio/submenu'
import { useEsAdminUi, useVerComo } from './ver-como'

const SNAP_INICIAL: SnapshotConexion = {
    reconectando: false,
    ultimoLatidoRecibidoEn: null,
    lastOkAt: null,
    serverNow: null,
}

function Enlace({ to, children }: { to: string; children: React.ReactNode }) {
    return (
        <NavLink
            to={to}
            end
            className={({ isActive }) =>
                isActive ? 'text-neutral-100' : 'text-neutral-500 hover:text-neutral-300'
            }
        >
            {children}
        </NavLink>
    )
}

export function Layout() {
    const queryClient = useQueryClient()
    const navigate = useNavigate()
    const { accountId } = useParams()
    const { pathname } = useLocation()
    const { data: sesion } = useSesion()
    const { vista, salir: salirDeVista } = useVerComo()
    const esAdminUi = useEsAdminUi()
    const gestionaUsuarios = esAdminUi && sesion?.gestionaUsuarios === true
    const miga = accountId ? migaAnterior(pathname, accountId) : null
    const idsCliente = sesion?.role === 'cliente' ? (sesion.accountIds ?? []) : null

    const [snap, setSnap] = useState<SnapshotConexion>(SNAP_INICIAL)
    const [ahora, setAhora] = useState<number>(() => Date.now())
    const [menuMano, setMenuMano] = useState(false)

    useEffect(() => {
        const cliente = new ClienteVivo({
            queryClient,
            onConexion: setSnap,
            onSesionExpirada: () => {
                queryClient.removeQueries({ queryKey: CLAVE_SESION })
                navigate('/login', { replace: true })
            },
        })
        cliente.conectar()
        return () => cliente.desconectar()
    }, [queryClient, navigate])

    // Re-evaluar el estado con el tiempo (para el umbral de socket encallado).
    useEffect(() => {
        const t = setInterval(() => setAhora(Date.now()), 3000)
        return () => clearInterval(t)
    }, [])

    const estado = useMemo(() => clasificarConexion(snap, ahora), [snap, ahora])

    async function salir() {
        if (vista) {
            salirDeVista()
            navigate('/', { replace: true })
            return
        }
        // Salir SIEMPRE lleva al login, aunque el logout del servidor falle: el
        // usuario quiere salir. El servidor revoca el sid; el cliente se limpia.
        try {
            await api.post('/auth/logout')
        } finally {
            salirDeVista()
            queryClient.removeQueries({ queryKey: CLAVE_SESION })
            queryClient.removeQueries({ queryKey: CLAVE_CONVERSACIONES })
            navigate('/login', { replace: true })
        }
    }

    if (vista && esRutaDeOperacion(pathname)) {
        return <Navigate to={`/business/${vista.accountId}`} replace />
    }

    const destinosMano = esAdminUi
        ? [
              { to: '/', etiqueta: 'Operación' },
              { to: '/health', etiqueta: 'Salud' },
              { to: '/altas/nueva', etiqueta: 'Alta' },
              { to: '/altas', etiqueta: 'En espera' },
              { to: '/altas/plantillas', etiqueta: 'Plantillas' },
              { to: '/altas/cuarentena', etiqueta: 'Cuarentena' },
              { to: '/retencion', etiqueta: 'Retención' },
              { to: '/mcp-facebook', etiqueta: 'MCP Facebook' },
              { to: '/whatsapp', etiqueta: 'WhatsApp' },
              { to: '/accesos', etiqueta: 'Accesos' },
              ...(gestionaUsuarios ? [{ to: '/usuarios', etiqueta: 'Usuarios' }] : []),
              { to: '/perfil', etiqueta: 'Perfil' },
          ]
        : [
              ...(idsCliente ?? []).map((id) => ({
                  to: `/business/${id}`,
                  etiqueta: idsCliente && idsCliente.length === 1 ? 'Mi negocio' : `Negocio #${id}`,
              })),
              { to: '/perfil', etiqueta: 'Perfil' },
          ]

    return (
        <HilosFlotantesProvider>
        <div className="min-h-screen bg-neutral-950 font-sans text-neutral-200">
            <div className="chrome-barra sticky top-0 z-30 border-b border-neutral-800 bg-neutral-950/95 backdrop-blur">
            <header className="flex items-center justify-between gap-3 px-[max(1rem,env(safe-area-inset-left))] py-2 pr-[max(1rem,env(safe-area-inset-right))] pt-[max(0.5rem,env(safe-area-inset-top))] md:px-6 md:py-3">
                <div className="flex min-w-0 items-baseline gap-4">
                    <h1 className="shrink-0 text-sm font-semibold tracking-tight text-neutral-100">
                        {esAdminUi ? 'Agendify · Operación' : 'Agendify'}
                    </h1>
                    <nav className="hidden items-center gap-3 text-sm md:flex">
                        {/* Nivel 1 solo admin. Cliente: selector de sus negocios. */}
                        {esAdminUi && (
                            <>
                                <Enlace to="/">Operación</Enlace>
                                <Enlace to="/health">Salud</Enlace>
                                <Enlace to="/altas/nueva">Alta</Enlace>
                                <Enlace to="/altas">En espera</Enlace>
                                <Enlace to="/altas/plantillas">Plantillas</Enlace>
                                <Enlace to="/altas/cuarentena">Cuarentena</Enlace>
                                <Enlace to="/retencion">Retención</Enlace>
                                <Enlace to="/mcp-facebook">MCP Facebook</Enlace>
                                <Enlace to="/whatsapp">WhatsApp</Enlace>
                                <Enlace to="/accesos">Accesos</Enlace>
                                {gestionaUsuarios && <Enlace to="/usuarios">Usuarios</Enlace>}
                            </>
                        )}
                        {idsCliente && idsCliente.length > 1 && (
                            <label className="flex items-center gap-2 text-neutral-500">
                                Negocio
                                <select
                                    aria-label="Selector de negocio"
                                    className="rounded border border-neutral-700 bg-neutral-900 px-2 py-0.5 text-neutral-200"
                                    value={accountId ?? String(idsCliente[0])}
                                    onChange={(e) => navigate(`/business/${e.target.value}`)}
                                >
                                    {idsCliente.map((id) => (
                                        <option key={id} value={id}>
                                            #{id}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        )}
                        {idsCliente && idsCliente.length === 1 && (
                            <Enlace to={`/business/${idsCliente[0]}`}>Mi negocio</Enlace>
                        )}
                    </nav>
                </div>
                <div className="flex shrink-0 items-center gap-2 md:gap-5">
                    <RefrescoCabecera />
                    <ConnectionIndicator estado={estado} />
                    {sesion && (
                        <Link
                            to="/perfil"
                            className="hidden text-xs text-neutral-500 hover:text-neutral-200 md:inline"
                        >
                            {sesion.email}
                        </Link>
                    )}
                    <Button variant="ghost" size="sm" className="hidden md:inline-flex" onClick={() => void salir()}>
                        {vista ? 'Cerrar sesión' : 'Salir'}
                    </Button>
                    <button
                        type="button"
                        className="flex h-11 w-11 items-center justify-center rounded-md text-neutral-200 hover:bg-neutral-900 md:hidden"
                        aria-label="Abrir menú"
                        aria-expanded={menuMano}
                        onClick={() => setMenuMano(true)}
                    >
                        <span className="flex flex-col gap-1" aria-hidden>
                            <span className="block h-0.5 w-5 bg-neutral-200" />
                            <span className="block h-0.5 w-5 bg-neutral-200" />
                            <span className="block h-0.5 w-5 bg-neutral-200" />
                        </span>
                    </button>
                </div>
            </header>
            {vista && (
                <div
                    className="flex flex-wrap items-center justify-between gap-3 border-t border-sky-900/50 bg-sky-950/60 px-[max(1rem,env(safe-area-inset-left))] py-2 pr-[max(1rem,env(safe-area-inset-right))] md:px-6"
                    data-testid="ver-como-banner"
                >
                    <p className="text-sm text-sky-100">
                        Estás viendo el panel de <strong>{vista.nombre}</strong>
                    </p>
                    <button
                        type="button"
                        onClick={() => {
                            salirDeVista()
                            navigate('/', { replace: true })
                        }}
                        className="rounded-md border border-sky-400/40 bg-sky-500/15 px-3 py-1 text-sm font-medium text-sky-100 hover:bg-sky-500/25"
                    >
                        Salir
                    </button>
                </div>
            )}
            {accountId && <SubmenuNegocio accountId={accountId} />}
            </div>
            <main className="px-[max(1rem,env(safe-area-inset-left))] py-4 pr-[max(1rem,env(safe-area-inset-right))] pb-[max(1.25rem,env(safe-area-inset-bottom))] md:px-6 md:py-5">
                {miga && (
                    <Link
                        to={miga.to}
                        className="mb-4 inline-flex min-h-11 items-center text-sm text-neutral-500 hover:text-neutral-200"
                    >
                        ← {miga.etiqueta}
                    </Link>
                )}
                <Outlet />
            </main>
            <MenuMano
                abierto={menuMano}
                onCerrar={() => setMenuMano(false)}
                destinos={destinosMano}
                correo={sesion?.email ?? null}
                etiquetaSalir={vista ? 'Cerrar sesión' : 'Salir'}
                onSalir={() => {
                    setMenuMano(false)
                    void salir()
                }}
            />
        </div>
        </HilosFlotantesProvider>
    )
}
